import { startOfDayInTimezone } from '@alliance-disposal/full-stack-utils';
import { endOfDay, format, startOfDay } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { DISPLAY_DATE_FORMAT } from '../formatter';
export { startOfDayInTimezone } from '@alliance-disposal/full-stack-utils';
export const endOfDayInTimezone = (date, timezone) => {
    const zonedDate = toZonedTime(date, timezone);
    const endOfDayZoned = endOfDay(zonedDate);
    return fromZonedTime(endOfDayZoned, timezone);
};
/**
 * Compare two dates to see if they fall on the same calendar day in a specific timezone.
 * Unlike date-fns isSameDay which uses browser local time, this compares in the given timezone.
 */
export const isSameDayInTimezone = (date1, date2, timezone) => startOfDayInTimezone(date1, timezone).getTime() === startOfDayInTimezone(date2, timezone).getTime();
export const formatDayInTimezone = (isoInstant, timezone, pattern = DISPLAY_DATE_FORMAT) => {
    if (!isoInstant)
        return undefined;
    const instant = new Date(isoInstant);
    if (Number.isNaN(instant.getTime()))
        return undefined;
    return formatInTimeZone(instant, timezone, pattern);
};
// Converts UTC offset in minutes to +/-HH:mm format, e.g., -240 -> -04:00
export const formatUtcOffset = (minutes) => {
    // If `minutes` is undefined, take a best guess using the client's configured time zone via Date getTimezoneOffset(),
    // which must be negated to reflect the accurate offset
    const minsToCompute = minutes ?? -new Date().getTimezoneOffset();
    const sign = minsToCompute <= 0 ? '-' : '+';
    const abs = Math.abs(minsToCompute);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    return `${sign}${hh}:${mm}`;
};
// react-day-picker compares Y/M/D in browser-local time, but our schedule and task ISOs are
// anchored to the hauler's timezone. Without these conversions the picker drifts by a day
// whenever the browser tz and hauler tz disagree.
/** Hauler-tz Y/M/D of `instant`, materialized as a browser-local Date so the calendar selects it. */
export const toCalendarDateInTimezone = (instant, timezone) => {
    const [y, m, d] = formatInTimeZone(instant, timezone, 'yyyy-MM-dd').split('-').map(Number);
    return new Date(y, m - 1, d);
};
/** Reinterpret a calendar's local-midnight Date as the `HH:mm` wall-clock `time` (midnight by default) in
 *  `timezone`. `fromZonedTime` reads the Date's **local** fields as the wall clock in `timezone`, so the
 *  reference Date is built with `new Date(y, m, d, h, min)` (local), not `Date.UTC(...)`. */
export const calendarDateToZonedTime = (date, timezone, time = '00:00') => {
    const [hours, minutes] = time.split(':').map(Number);
    return fromZonedTime(new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes), timezone);
};
/**
 * Parse a `yyyy-MM-dd` string as noon UTC. Anchoring at noon UTC keeps the date inside the same
 * calendar day worldwide, avoiding off-by-one drift when the value is later interpreted in a
 * specific timezone.
 */
export const parseYmdAsNoonUtc = (ymd) => {
    if (!ymd)
        return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!match)
        return null;
    const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
        return null;
    return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
};
/** The calendar day `day` names in the local timezone, anchored at noon UTC like `parseYmdAsNoonUtc`. */
export const toNoonUtcCalendarDay = (day) => parseYmdAsNoonUtc(format(day, 'yyyy-MM-dd')) ?? day;
/** Today in `timezone`, anchored at noon UTC like `parseYmdAsNoonUtc`. */
export const getTodayAsNoonUtc = (timezone) => parseYmdAsNoonUtc(formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd')) ?? startOfDay(new Date());
//# sourceMappingURL=dates-time-zone.js.map